import type {
  AccountStatus,
  AppBootstrap,
  AppBridge,
  ClientLogEvent,
  CreatePaymentInput,
  DispatchSurfaceActionInput,
  Instruction,
  PaymentResult,
  SurfaceModel,
  SupportLink,
  Tariff,
} from "@paxvpn-cabinet/domain";

interface TelegramWebApp {
  initData: string;
  platform?: string;
  version?: string;
  ready?: () => void;
  expand?: () => void;
  openLink?: (url: string) => void;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

const INIT_DATA_STORAGE_KEY = "paxvpn.telegram.initData";

export function getTelegramInitData(): string {
  const freshInitData = getFreshTelegramInitData();
  if (freshInitData) {
    writeStoredInitData(freshInitData);
    return freshInitData;
  }

  return readStoredInitData();
}

export function hasTelegramRuntime(): boolean {
  return (
    Boolean(getTelegramInitData()) ||
    hasTelegramLaunchParams(window.location) ||
    isTelegramPlatform(window.Telegram?.WebApp?.platform)
  );
}

export function describeTelegramLaunchContext(): string {
  const webApp = window.Telegram?.WebApp;
  const hashKeys = getTelegramParamKeys(window.location.hash);
  const searchKeys = getTelegramParamKeys(window.location.search);
  const details = [
    `sdk=${webApp ? "yes" : "no"}`,
    `platform=${webApp?.platform ?? "none"}`,
    `sdkInitData=${webApp?.initData ? "yes" : "no"}`,
    `urlInitData=${getTelegramInitDataFromLocation(window.location) ? "yes" : "no"}`,
    `storedInitData=${readStoredInitData() ? "yes" : "no"}`,
    `hashKeys=${hashKeys.length ? hashKeys.join(",") : "none"}`,
    `searchKeys=${searchKeys.length ? searchKeys.join(",") : "none"}`,
  ];
  return details.join("; ");
}

function getFreshTelegramInitData(): string {
  return window.Telegram?.WebApp?.initData || getTelegramInitDataFromLocation(window.location);
}

function getTelegramInitDataFromLocation(location: Location): string {
  return getTelegramInitDataFromParams(location.hash) || getTelegramInitDataFromParams(location.search);
}

function getTelegramInitDataFromParams(value: string): string {
  const params = new URLSearchParams(value.replace(/^[?#]/, ""));
  return params.get("tgWebAppData") ?? "";
}

function hasTelegramLaunchParams(location: Location): boolean {
  return hasTelegramLaunchParam(location.hash) || hasTelegramLaunchParam(location.search);
}

function hasTelegramLaunchParam(value: string): boolean {
  const params = new URLSearchParams(value.replace(/^[?#]/, ""));
  return params.has("tgWebAppVersion") || params.has("tgWebAppPlatform") || params.has("tgWebAppData");
}

function isTelegramPlatform(platform: string | undefined): boolean {
  return Boolean(platform && platform !== "unknown");
}

function getTelegramParamKeys(value: string): string[] {
  const params = new URLSearchParams(value.replace(/^[?#]/, ""));
  return Array.from(params.keys()).filter((key) => key.startsWith("tgWebApp"));
}

function readStoredInitData(): string {
  try {
    return window.sessionStorage.getItem(INIT_DATA_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function writeStoredInitData(initData: string): void {
  try {
    window.sessionStorage.setItem(INIT_DATA_STORAGE_KEY, initData);
  } catch {
    // Telegram WebView may disable storage in constrained modes.
  }
}

export class TelegramBridge implements AppBridge {
  constructor(
    private readonly apiBaseUrl = "/api/cabinet",
    private readonly webApp: TelegramWebApp | undefined = window.Telegram?.WebApp,
  ) {
    this.webApp?.ready?.();
    this.webApp?.expand?.();
  }

  bootstrap(): Promise<AppBootstrap> {
    return this.request<AppBootstrap>("bootstrap");
  }

  getAccountStatus(): Promise<AccountStatus> {
    return this.request<AccountStatus>("getAccountStatus");
  }

  getSurface(surfaceId: string): Promise<SurfaceModel> {
    return this.request<SurfaceModel>("getSurface", { surfaceId });
  }

  dispatchAction(input: DispatchSurfaceActionInput): Promise<SurfaceModel> {
    return this.request<SurfaceModel>("dispatchAction", input);
  }

  getTariffs(): Promise<Tariff[]> {
    return this.request<{ tariffs: Tariff[] }>("getTariffs").then((payload) => payload.tariffs);
  }

  async createPayment(input: CreatePaymentInput): Promise<PaymentResult> {
    const result = await this.request<PaymentResult>("createPayment", input);
    this.openExternalLink(result.paymentUrl);
    return result;
  }

  getInstruction(): Promise<Instruction> {
    return this.request<Instruction>("getInstruction");
  }

  getSupportLink(): Promise<SupportLink> {
    return this.request<SupportLink>("getSupportLink");
  }

  async logClientEvent(event: ClientLogEvent): Promise<void> {
    await this.request("logClientEvent", event);
  }

  openExternalLink(url: string): void {
    if (this.webApp?.openLink) {
      this.webApp.openLink(url);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  }

  private async request<T>(action: string, data: unknown = {}): Promise<T> {
    const initData = this.webApp?.initData || getTelegramInitData();
    if (!initData) {
      throw new Error(`Telegram initData отсутствует. ${describeTelegramLaunchContext()}`);
    }

    const response = await fetch(`${this.apiBaseUrl}/${action}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Telegram-Init-Data": initData,
      },
      body: JSON.stringify({ data }),
    });
    const payload = await response.json();
    if (!response.ok || payload.ok === false) {
      throw new Error(payload.message ?? "Cabinet API error.");
    }
    return payload.data as T;
  }
}
